// 录播弹题 cue 调度纯函数（零依赖：不 import Vue / @saas/service-api，供 InteractionLayer.vue 消费）。
//
// 测试在 packages/interactions/tests/interaction-cue.spec.js（pages 包无 jest 跑道，
// 本文件零依赖故可被跨包相对路径 import 测试：
// import { buildCues, shouldFire } from '../../pages/src/video-play/components/replay/components/interaction-cue.js'
// ESM export 经 packages/interactions/babel.config.js（@babel/preset-env, modules:'auto'）
// 转译为 CJS 后由 babel-jest 执行，与本包其余 src 文件的 export 写法保持一致。
//
// ============================================================================
// video_index 基准（易错，改动前先读这段——task brief 原文把它标了问号，这里是实测结论）：
// ============================================================================
// 后端 lesson_interaction.video_index 是"多段视频段索引"（saas-api/internal/model/
// lesson_interaction.go:32 字段注释），存的是 admin 保存时传入的 req.VideoIndex 原样
// （interaction_admin.go:141 `li.VideoIndex = req.VideoIndex`，无任何 +1/-1 变换）。
// 实测证据（saas-api/internal/service/interaction_admin_test.go）：
//   - "单段课时非0video_index_拒绝"（:567-596）：单段课时强制 VideoIndex=0，传 1 直接
//     报错——证明单段课时的合法 video_index 就是 0。
//   - "多段课时不做段级精确钳制"（:538-557）：保存"第二段"的题传 VideoIndex=1。
// 即 video_index 是 **0 基**，与 InteractionLayer 的 videoIndex prop（= replay/Index.vue
// 的 currentIndex，同样 0 基）**直接相等比较，不做 +1 偏移**。
//
// 这与心跳表 course_heartbeat.video_index（1 基，= currentIndex+1，replay/Index.vue:
// 247-263 callKeepAlive 里 `video_index: this.currentIndex + 1`）是两个不同表的不同字段、
// 不同语义——plan 文档里"cue 过滤用 item.video_index === videoIndex + 1"的猜测是望文生义
// 套用了心跳的 +1 规则，经上述实测证伪，此处改为 `item.video_index === videoIndex`。
// ============================================================================

// state.event 取值（saas-api/internal/model/user_interaction_record.go:15-17）：
// 0=已触发未作答 / 1=已作答 / 2=已跳过。已作答/已跳过的题不应再次弹出。
const EVENT_ANSWERED = 1;
const EVENT_SKIPPED = 2;
const ANSWERED_OR_SKIPPED = [EVENT_ANSWERED, EVENT_SKIPPED];

// TextTrack cue 触发窗口宽度（秒）：track cue 本身用 [triggerSecond, triggerSecond+0.5]
// （挂 track 时用），shouldFire 的窗口稍宽（下面按 1s 判），给 timeupdate 兜底轮询的
// tick 间隔留余量，避免整秒边界抖动漏判。
const FIRE_WINDOW_SECONDS = 1;

/**
 * 按当前段过滤 + 分组 + 排序，构建可调度的 cue 分组列表。
 *
 * @param {Array} items - getInteractionList 返回的条目数组（每项含 video_index /
 *   trigger_second / sort / state.event 等字段，形状见 saas-api BuildStudentView +
 *   InteractionService.List）。
 * @param {number} videoIndex - 当前段，0 基（与 item.video_index 同基准，见上方大注释）。
 * @returns {Array<{triggerSecond: number, items: Array, fired: boolean}>} 按
 *   triggerSecond 升序排列的分组数组；组内 items 按 sort 升序排列。fired 初始为
 *   false，由调用方（InteractionLayer）在触发后原地置 true 做防重复。
 */
export function buildCues(items, videoIndex) {
  if (!Array.isArray(items)) return [];

  const groupsBySecond = new Map();

  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (item.video_index !== videoIndex) return;

    const event = item.state && item.state.event;
    if (ANSWERED_OR_SKIPPED.indexOf(event) !== -1) return;

    const triggerSecond = Number(item.trigger_second);
    if (!Number.isFinite(triggerSecond) || triggerSecond < 0) return;

    if (!groupsBySecond.has(triggerSecond)) {
      groupsBySecond.set(triggerSecond, {
        triggerSecond,
        items: [],
        fired: false,
      });
    }
    groupsBySecond.get(triggerSecond).items.push(item);
  });

  const groups = Array.from(groupsBySecond.values());
  groups.forEach((group) => {
    group.items.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
  });
  groups.sort((a, b) => a.triggerSecond - b.triggerSecond);
  return groups;
}

/**
 * 判定一个 cue 分组当前是否应该触发弹题。
 *
 * 窗口判定：currentTime 落在 [triggerSecond, triggerSecond + FIRE_WINDOW_SECONDS) 内
 * 即命中；已 fired 的组永远不再触发（防重复——cuechange 主路径与 timeupdate 兜底路径
 * 共享同一个 group 引用，任一路径触发后调用方将 group.fired 置 true，另一路径的判定
 * 自然短路）。
 *
 * 断点续播场景（currentTime 起播即已超过窗口）天然不触发：调用方从未在窗口内调用过
 * shouldFire，不会有"过期补弹"的副作用。
 *
 * @param {{triggerSecond: number, fired: boolean}} group
 * @param {number} currentTime - 当前播放秒数
 * @returns {boolean}
 */
export function shouldFire(group, currentTime) {
  if (!group || group.fired) return false;
  if (typeof currentTime !== "number" || !Number.isFinite(currentTime))
    return false;
  const t = group.triggerSecond;
  return currentTime >= t && currentTime < t + FIRE_WINDOW_SECONDS;
}

export { EVENT_ANSWERED, EVENT_SKIPPED, FIRE_WINDOW_SECONDS };
